import React, { Component } from 'react';

import moment from 'moment';
import { getAgentDisplayName } from '../../../utils/agentUtil';
import { requestMobileView } from '../../../utils/mobileUtil';
import {
  getObservationTypeMeta,
  shouldShowResultTag,
  normalizeTagLabel
} from '../../../utils/observationTypeUtil';

import './Observation.scss';

const capitalize = s => (s ? s[0].toUpperCase() + s.slice(1) : '');
const truncateTagLabel = value => {
  const text = (value || '').toString().trim();
  if (text.length <= 24) return text;
  return `${text.slice(0, 24)}...`;
};
const extractTagLabel = value => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'object') {
    const candidate = [value.label, value.name, value.value].find(
      item => item !== undefined && item !== null && String(item).trim()
    );
    return candidate ? String(candidate).trim() : '';
  }
  return String(value).trim();
};
const getResultTagLabels = item => {
  const labels = [];
  const seen = new Set();
  const pushUniqueLabel = value => {
    const label = extractTagLabel(value);
    if (!label || !shouldShowResultTag(label)) return;
    const dedupeKey =
      normalizeTagLabel(label) ||
      label
        .toString()
        .trim()
        .toLowerCase();
    if (!dedupeKey || seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    labels.push(label);
  };
  const concepts = Array.isArray(item && item.concepts)
    ? item.concepts
    : item && item.concepts
    ? [item.concepts]
    : [];
  concepts.forEach(concept => {
    pushUniqueLabel(concept);
  });
  const tags = Array.isArray(item && item.tags)
    ? item.tags
    : item && item.tags
    ? [item.tags]
    : [];
  tags.forEach(tag => {
    pushUniqueLabel(tag);
  });
  return labels;
};
const IMAGE_MEDIA_TYPES = new Set([
  'jpeg',
  'jpg',
  'png',
  'gif',
  'webp',
  'bmp',
  'tiff',
  'heic',
  'heif'
]);
const isImageMediaType = mediaType => {
  const value = String(mediaType || '')
    .trim()
    .toLowerCase();
  if (!value) return false;
  return value.startsWith('image/') || IMAGE_MEDIA_TYPES.has(value);
};

class ObservationItem extends Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      photosOpen: true,
      graphOpen: false,
      detailsOpen: true,
      allDataOpen: false,
      filesOpen: false,
      UTM: false
    };
  }

  handleSwitch = value => {
    this.setState({ UTM: value });
  };

  render() {
    const { item, ...props } = this.props;
    const agentDisplayName = getAgentDisplayName(item);
    const hasFriendlyAgentName =
      item &&
      item.agent &&
      item.agent.agentName &&
      String(item.agent.agentName).trim() !== '';
    const observationClass = (item.observationClass || '')
      .toString()
      .trim()
      .toLowerCase();
    const observationClassIconClass = observationClass.includes('alert')
      ? 'observation-type-alert'
      : observationClass.includes('note')
      ? 'observation-type-note'
      : '';
    const observationClassTextClass = observationClass.includes('alert')
      ? 'observation-class-alert'
      : observationClass.includes('note')
      ? 'observation-class-note'
      : '';
    const observationTypeRaw = (item.observationType || '').toString();
    const observationTypeMeta = getObservationTypeMeta(observationTypeRaw);
    if (Array.isArray(item.where)) {
      item.where = item.where[0]; // [GJo] not supposed to be an Array
    }
    const mediaArray = Array.isArray(item.media) ? item.media : [];
    const mediaCountKnown = true;
    const mediaCount = mediaArray.length;
    const hasMedia = mediaCount > 0;
    const imageMedia = mediaArray.filter(
      mediaItem =>
        mediaItem &&
        mediaItem.Media &&
        isImageMediaType(mediaItem.Media.mediaType)
    );
    const resultTagLabels = getResultTagLabels(item);
    return (
      <div
        id={item.observationId}
        className={`result-item card container rounded-0 border-left-0 border-top-0 border-right-0 border-bottom p-0 ${
          props.selected ? 'selected' : ''
        } ${props.viewed ? 'viewed' : ''} ${
          props.linkedToSelectedTrack ? 'linked-track-observation' : ''
        }`}
        onClick={() => {
          props.setSelectedItem(item);
          if (props.goToMapOnSelect) requestMobileView('map');
        }}
      >
        <div className="row no-gutters">
          <div className="col-auto  pl-2 pr-3 pt-3 pb-3 ml-1 photoItem">
            <div
              className={`attachment-container ${
                hasMedia
                  ? 'has-media'
                  : mediaCountKnown
                  ? 'no-media'
                  : 'unknown-media'
              }`}
              role="button"
              onClick={e => {
                e.stopPropagation();
                if (props.onOpenMedia) {
                  props.onOpenMedia(item);
                  return;
                }
                if (imageMedia.length > 0) {
                  props.setSelectedItem(item);
                  props.openImage({
                    images: imageMedia,
                    index: 0
                  });
                }
              }}
            >
              <span className="camera-badge-icon" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4.5 7.5H8L9.8 5.5H14.2L16 7.5H19.5C20.6 7.5 21.5 8.4 21.5 9.5V17.5C21.5 18.6 20.6 19.5 19.5 19.5H4.5C3.4 19.5 2.5 18.6 2.5 17.5V9.5C2.5 8.4 3.4 7.5 4.5 7.5Z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle
                    cx="12"
                    cy="13.2"
                    r="3.2"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />
                </svg>
                {!hasMedia && mediaCountKnown && (
                  <span className="camera-badge-slash" />
                )}
              </span>
              {hasMedia && mediaCountKnown && (
                <span className="number">{mediaCount}</span>
              )}
            </div>
          </div>
          <div className="col">
            <div className="card-block pl-0 pr-3 py-2 pt-3">
              {props.linkedToSelectedTrack && (
                <p className="linked-track-observation-label mb-1">
                  Linked observation
                </p>
              )}
              <h6 className="card-title mb-2 mt-1 font-weight-bold pointer">
                <span className="observation-title-with-icon">
                  {observationTypeMeta.icon && (
                    <img
                      src={observationTypeMeta.icon}
                      className={`observation-type ${observationClassIconClass}`}
                      alt={observationTypeMeta.iconAlt}
                    />
                  )}
                  {item.observationClass ? (
                    <span
                      className={`observation-class-label ${observationClassTextClass}`}
                    >
                      {capitalize(item.observationClass)}
                    </span>
                  ) : null}
                  <span>{observationTypeMeta.label}</span>
                </span>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm float-right btn-collapse"
                  data-toggle="collapse"
                  href="#multiCollapseExample2"
                  aria-expanded="false"
                  aria-controls="multiCollapseExample1"
                  onClick={e => {
                    e.stopPropagation();
                    props.setSelectedItem(item);
                    props.openObservation(item);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-box-arrow-up-right"
                    viewBox="0 0 16 16"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5z"
                    />
                    <path
                      fillRule="evenodd"
                      d="M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0v-5z"
                    />
                  </svg>
                </button>
              </h6>
              <p className="card-text mb-1 text-muted font-weight-light">
                <span>{moment(item.when).format('DD MMM YYYY - HH:mm')}</span>
                {agentDisplayName ? (
                  <span>
                    {' '}
                    •{' '}
                    {hasFriendlyAgentName
                      ? capitalize(agentDisplayName)
                      : agentDisplayName}
                  </span>
                ) : (
                  <span></span>
                )}
              </p>
              <p className="card-text mb-2 text-muted font-weight-light">
                {item.description || 'No description'}
              </p>
              {resultTagLabels.map((label, key) => (
                <span
                  key={key}
                  className="badge badge-secondary p-2 mr-1 mb-1 text-uppercase"
                >
                  {truncateTagLabel(label)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ObservationItem;
